<script setup lang="ts" generic="T extends Array<Record<string, any>>">
const props = defineProps<{ items: T }>();
const container = useTemplateRef('container');
const parent = useTemplateRef('parent');
const tabs = useTemplateRef('tabs');
const indicator = useTemplateRef('indicator');
const activeItem = defineModel<typeof props.items[number]>();
const activeIndex = ref(0);
const interval = useInterval(12000, {
    controls: true,
    callback: () => nextItem(),
});

function scrollIntoViewActiveTab(elem: HTMLHeadingElement | undefined) {
    if (!elem || !tabs.value || !container.value) return;

    const containerRect = container.value.getBoundingClientRect();
    const elemRect = elem.getBoundingClientRect();
    const offset = elemRect.left - containerRect.left - containerRect.width / 2 + elemRect.width / 2;
    container.value?.scrollBy({ left: offset });
};

type TItem = typeof props.items[number];
function activeTab(item: TItem | undefined, index: number) {
    if (!tabs.value || !item) return;

    activeIndex.value = index;
    activeItem.value = item;
    moveActiveIndicator(parent, indicator, tabs, index);
    scrollIntoViewActiveTab(tabs.value[index]);    
}

function handleOnClickTab(item: TItem, index: number) {
    interval.pause();
    activeTab(item, index);
    interval.reset();
    interval.resume();
};

function nextItem() {
    if (!props.items?.length || !tabs.value) return;

    const index = (activeIndex.value + 1) % props.items.length;
    activeTab(props.items[index], index);
};

onMounted(async () => {
    if (!props.items?.length || !tabs.value?.length) return;

    await nextTick();
    activeTab(props.items[0], 0);
});
</script>

<template>
    <div ref="container" class="w-full overflow-y-hidden no-scrollbar h-14.5 bg-gray-100/15 rounded-full my-10">
        <div ref="parent" class="w-max relative top-2 right-2 flex items-center pl-2">
            <svg ref="indicator" class="absolute h-10 bg-white transition-transform duration-500 rounded-full right-0"></svg>
            <h3 
                v-for="item, index in props.items"
                :key="item.title"
                ref="tabs"
                class="w-fit py-2 px-4 relative z-1 flex justify-center items-center cursor-pointer text-body1-bold 
                transition-colors duration-300 select-none"
                :class="activeItem?.title === item.title && 'text-primary-600'"
                @click="handleOnClickTab(item, index)"
            >
                {{ item.title }}
            </h3>
        </div>
    </div>
</template>